import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { SiteQr } from "./QrCode";
import { useLang } from "../i18n/LanguageContext";

const NAV = [
  { to: "/servizi", id: "servizi" },
  { to: "/lavori", id: "lavori" },
  { to: "/recensioni", id: "recensioni" },
  { to: "/chi-siamo", id: "chiSiamo" },
  { to: "/contatti", id: "contatti" },
];

const LangToggle = ({ mobile = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-line bg-white p-1 ${mobile ? "self-start" : ""}`} data-testid="language-toggle">
      {["it", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
            lang === l ? "bg-brand text-white" : "text-slate-500 hover:text-brand"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const { t } = useLang();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-300 hover:text-brand ${
      isActive ? "text-brand" : "text-slate-600"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled ? "glass border-b border-line shadow-sm" : "border-b border-transparent bg-white/60 backdrop-blur-md"
      }`}
    >
      <motion.div className="absolute inset-x-0 top-0 h-0.5 origin-left bg-brand" style={{ scaleX: progress }} data-testid="scroll-progress" />
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-5 sm:px-8">
        <div className="flex items-center gap-3">
          <Link to="/" aria-label="Homepage" data-testid="nav-home-link">
            <Logo className="h-14 sm:h-16" />
          </Link>
          <button
            onClick={() => setQrOpen(true)}
            className="hidden transition-transform duration-300 hover:scale-110 sm:block"
            aria-label={t.nav.qrHint}
            title={t.nav.qrHint}
            data-testid="qr-open-button"
          >
            <SiteQr size={46} />
          </button>
        </div>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink key={item.id} to={item.to} className={linkCls} data-testid={`nav-link-${item.id.toLowerCase()}`}>
              {t.nav[item.id]}
            </NavLink>
          ))}
          <LangToggle />
          <Link
            to="/contatti"
            data-testid="header-quote-cta"
            className="group flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-amber"
          >
            {t.nav.cta}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle mobile />
          <button
            className="text-slate-800"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-button"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="glass overflow-hidden border-b border-line lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.id.toLowerCase()}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-left text-base font-medium transition-colors hover:bg-surface2 hover:text-brand ${
                      isActive ? "text-brand" : "text-slate-700"
                    }`
                  }
                >
                  {t.nav[item.id]}
                </NavLink>
              ))}
              <Link
                to="/contatti"
                onClick={() => setOpen(false)}
                data-testid="mobile-quote-cta"
                className="mt-2 rounded-full bg-brand px-5 py-3 text-center text-sm font-semibold text-white"
              >
                {t.nav.cta}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {qrOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-5 backdrop-blur-sm"
            onClick={() => setQrOpen(false)}
            data-testid="qr-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-line bg-white p-8 text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="qr-modal"
            >
              <button
                onClick={() => setQrOpen(false)}
                className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-surface2 hover:text-slate-900"
                aria-label="Chiudi"
                data-testid="qr-modal-close"
              >
                <X size={18} />
              </button>
              <SiteQr size={220} />
              <p className="mt-5 max-w-[240px] text-sm font-medium text-slate-700">{t.nav.qrHint}</p>
              <p className="mt-1 text-xs text-slate-400">{window.location.host}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
